/* eslint-disable mobx/missing-observer */
import { lightColors as colors } from "@common/consts/colors";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import classNames from "classnames";
import React from "react";

import { BeatLoader } from "react-spinners";
import { twMerge } from "tailwind-merge";

const buttonClass = cva("rounded-[4px] leading-4", {
  variants: {
    mode: {
      default: "",
      contrast: "",
    },
    color: {
      primary: "",
      secondary: "",
      yellow: "bg-yellow-2 hover:bg-yellow-3 text-permanent-black",
      blue: "bg-blue-3 hover:bg-blue-4 text-permanent-white",
      red: "",
      none: "",
    },
    size: {
      small: "py-1 px-2 text-xs gap-2",
      medium: "py-2 px-3 text-sm gap-2",
    },
    variant: {
      solid: "",
      transparent: "",
      dead: "",
    },
    disabled: {
      true: "pointer-events-none opacity-50",
      false: "",
    },
  },
  compoundVariants: [
    {
      mode: "default",
      color: "primary",
      class: "text-glyph-prm",
    },
    {
      mode: "default",
      color: "secondary",
      class: "text-glyph-scd",
    },
    {
      mode: "contrast",
      color: "primary",
      class: "text-glyph-cst-prm",
    },
    {
      mode: "contrast",
      color: "secondary",
      class: "text-glyph-cst-prm",
    },
    {
      mode: "default",
      color: "primary",
      variant: "solid",
      class: "bg-surface-prm hover:bg-surface-scd",
    },
    {
      mode: "default",
      color: "secondary",
      variant: "solid",
      class: "bg-surface-scd hover:bg-surface-trt",
    },
    {
      mode: "contrast",
      color: "primary",
      variant: "solid",
      class: "bg-surface-cst hover:bg-surface-cst-scd ",
    },
    {
      mode: "contrast",
      color: "secondary",
      variant: "solid",
      class: "bg-surface-cst-scd hover:bg-surface-cst",
    },
    {
      mode: "default",
      color: "primary",
      variant: "transparent",
      class: "hover:bg-surface-scd",
    },
    {
      mode: "default",
      color: "secondary",
      variant: "transparent",
      class: "hover:bg-surface-scd",
    },
    {
      mode: "contrast",
      color: "primary",
      variant: "transparent",
      class: "hover:bg-surface-cst-scd",
    },
    {
      mode: "contrast",
      color: "secondary",
      variant: "transparent",
      class: "hover:bg-surface-cst-scd",
    },
    {
      color: "red",
      variant: "transparent",
      class: "text-red-3 hover:bg-surface-scd",
    },
    {
      color: "red",
      variant: "solid",
      class: "text-permanent-white bg-red-3 hover:bg-red-4",
    },
  ],
  defaultVariants: {
    mode: "default",
    size: "medium",
    color: "primary",
    variant: "transparent",
  },
});

export type ButtonProps = VariantProps<typeof buttonClass> &
  Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "ref"> & {
    isLoading?: boolean;
    size?: "small" | "medium";
    leadingIcon?: JSX.Element;
    trailingIcon?: JSX.Element;
  };

export const Button = React.forwardRef<HTMLDivElement, ButtonProps>(function Button(
  { isLoading, style, children, leadingIcon, trailingIcon, size, mode, color, variant, disabled, ...otherProps },
  forwardedRef,
) {
  const divRef = React.useRef<HTMLDivElement | null>(null);

  const computedColor = style?.color ?? (divRef.current ? getComputedStyle(divRef.current).color : colors["glyph-prm"]);

  return (
    <div
      ref={(ref) => {
        if (forwardedRef) {
          if (typeof forwardedRef === "function") {
            forwardedRef(ref);
          } else {
            forwardedRef.current = ref;
          }
        }

        divRef.current = ref;
      }}
      style={{
        userSelect: "none",
        ...style,
      }}
      {...otherProps}
      className={twMerge(
        "flex flex-row items-center cursor-pointer font-semibold whitespace-nowrap",
        buttonClass({ mode, size, color, variant, disabled }),
        otherProps.className,
      )}
      role="button"
      aria-disabled={disabled ?? false}
    >
      {leadingIcon}
      {isLoading ? <BeatLoader size={12} color={computedColor} /> : children}
      {trailingIcon}
    </div>
  );
});
